// Define the type of 'contenu' in ReeducationsOptions
export interface TextDifficulty {
  value: string;
  label: string;
  textes: string[];
}

// Define the structure for the parameters in each module
interface ModuleParams {
  vitesse?: { value: string; label: string }[];
  duree?: { value: string; label: string }[];
  contenu?: TextDifficulty[];
  taille?: { value: string; label: string }[];
  forme?: { value: string; label: string }[];
  nombre_par_hemichamp?: { value: string; label: string }[];
  couleur_cible?: { value: string; label: string }[];
  couleur_fond?: { value: string; label: string }[];
  side?: { value: string; label: string }[];
  mouvement?: { value: string; label: string }[];
  side_aveugle?: { value: string; label: string }[];
  nombre_cible?: { value: string; label: string }[];
  type_forme?: { value: string; label: string }[];
}

// Define the overall structure for ReeducationsOptions
interface ReeducationsOptionsType {
  ZF: {
    id: number;
    module: string;
    sous_module: string;
    parametre: ModuleParams;
    instructions: string;
  };
  ZPL: {
    id: number;
    module: string;
    sous_module: string;
    parametre: ModuleParams;
    instructions: string;
  };
  ZPI: {
    id: number;
    module: string;
    sous_module: string;
    parametre: ModuleParams;
    instructions: string;
  };
  ZPCCF: {
    id: number;
    module: string;
    sous_module: string;
    parametre: ModuleParams;
    instructions: string;
  };
  ZPFCF: {
    id: number;
    module: string;
    sous_module: string;
    parametre: ModuleParams;
    instructions: string;
  };
  CPD: {
    id: number;
    module: string;
    sous_module: string;
    parametre: null;
    contenu: string;
    instructions: null;
  };
  CN: {
    id: number;
    module: string;
    sous_module: string;
    parametre: null;
    duree: null;
    contenu: null;
    instructions: null;
  };
  CET: {
    id: number;
    module: string;
    sous_module: string;
    parametre: null;
    duree: null;
    contenu: null;
    instructions: null;
  };
  CGD: {
    id: number;
    module: string;
    sous_module: string;
    parametre: null;
    duree: null;
    contenu: null;
    instructions: null;
  };
  PVS: {
    id: number;
    module: string;
    sous_module: string;
    parametre: ModuleParams;
    instructions: string | null;
  };
  PVJ: {
    id: number;
    module: string;
    sous_module: string;
    parametre: null;
    duree: null;
    contenu: null;
    instructions: null;
  };
  OT: {
    id: number;
    module: string;
    sous_module: string;
    parametre: ModuleParams;
    instructions: string | null;
  };
  OB: {
    id: number;
    module: string;
    sous_module: string;
    parametre: ModuleParams;
    instructions: string | null;
  };
}

export const ReeducationsOptions: ReeducationsOptionsType = {
  ZF: {
    id: 1,
    module: "Champ visuel",
    sous_module: "Zones Frontières",
    parametre: {
      nombre_cible: [
        { value: "75", label: "75 cibles" },
        { value: "150", label: "150 cibles" },
        { value: "225", label: "225 cibles" },
      ],
    },
    instructions:
      "Fixez bien la croix centrale sans bouger les yeux. Appuyez sur la barre espace dès qu'une cible lumineuse apparait. Appuyez sur la barre espace pour lancer l'exercice.",
  },
  ZPL: {
    id: 2,
    module: "Champ visuel",
    sous_module: "Zones Profondes Localisation",
    parametre: {
      duree: [
        { value: "5", label: "5 min" },
        { value: "10", label: "10 min" },
        { value: "15", label: "15 min" },
        { value: "20", label: "20 min" },
        { value: "25", label: "25 min" },
        { value: "30", label: "30 min" },
        { value: "35", label: "35 min" },
        { value: "40", label: "40 min" },
        { value: "45", label: "45 min" },
        { value: "50", label: "50 min" },
        { value: "55", label: "55 min" },
        { value: "60", label: "60 min" },
      ],
      forme: [
        { value: "ronde", label: "Ronde" },
        { value: "carré", label: "Carré" },
      ],
      taille: [
        { value: "1", label: "Petit" },
        { value: "2", label: "Moyen" },
        { value: "3", label: "Grand" },
      ],
      nombre_par_hemichamp: [
        { value: "1", label: "1 cible" },
        { value: "3", label: "1-3 cibles" },
        { value: "6", label: "1-6 cibles" },
      ],
      couleur_cible: [
        { value: "white", label: "Blanc" },
        { value: "black", label: "Noir" },
        { value: "grey", label: "Gris" },
      ],
      couleur_fond: [
        { value: "white", label: "Blanc" },
        { value: "black", label: "Noir" },
        {
          value: "blue-green-yellow-purple-pink",
          label: "Aléatoire (bleu, vert, jaune, violet, rose)",
        },
        // { value: "fond scène", label: "Fond scène" },
      ],
      side: [
        { value: "unilatérale", label: "Unilatérale" },
        { value: "bilatérale", label: "Bilatérale" },
      ],
      mouvement: [
        { value: "true", label: "Oui" },
        { value: "false", label: "Non" },
      ],
    },
    instructions:
      "Fixez bien la croix au centre de l'écran sans bouger les yeux. Votre objectif est de pointer du doigt toutes les cibles que vous pourrez percevoir tout en maintenant la fixation au centre. Une fois une cible pointée, laissez votre doigt sur l'écran et vérifiez en regardant. Prenez autant de temps que nécessaire et n'hésitez pas à pointer à nouveau les cibles manquées en fixant bien à nouveau la croix centrale. Appuyez sur la barre espace pour passer à l'essai suivant. Si vous souhaitez faire une pause ne cours d'exercice, appuyez sur echap. Appuyez sur la barre espace pour lancer l'exercice.",
  },
  ZPI: {
    id: 3,
    module: "Champ visuel",
    sous_module: "Zones Profondes Identification",
    parametre: {
      duree: [
        { value: "5", label: "5 min" },
        { value: "10", label: "10 min" },
        { value: "15", label: "15 min" },
        { value: "20", label: "20 min" },
        { value: "25", label: "25 min" },
        { value: "30", label: "30 min" },
        { value: "35", label: "35 min" },
        { value: "40", label: "40 min" },
        { value: "45", label: "45 min" },
        { value: "50", label: "50 min" },
        { value: "55", label: "55 min" },
        { value: "60", label: "60 min" },
      ],
      taille: [
        { value: "1", label: "Petit" },
        { value: "2", label: "Moyen" },
        { value: "3", label: "Grand" },
      ],
      couleur_cible: [
        { value: "white", label: "Blanc" },
        { value: "colored", label: "Couleurs" },
      ],
      type_forme: [
        { value: "géométrique", label: "Géométrique" },
        { value: "barres", label: "Barres" },
        { value: "objets", label: "Objets" },
      ],
      nombre_par_hemichamp: [
        { value: "1", label: "1 cible" },
        { value: "3", label: "1-3 cibles" },
        { value: "6", label: "1-6 cibles" },
      ],
      side: [
        { value: "unilatérale", label: "Unilatérale" },
        { value: "bilatérale", label: "Bilatérale" },
      ],
      mouvement: [
        { value: "true", label: "Oui" },
        { value: "false", label: "Non" },
      ],
      couleur_fond: [
        { value: "white", label: "Blanc" },
        { value: "black", label: "Noir" },
        { value: "grey", label: "Gris" },
      ],
    },
    instructions:
      "Fixez bien la croix au centre de l'écran sans bouger les yeux. Votre objectif est de décrire et identifier toutes les cibles que vous pourrez percevoir tout en maintenant la fixation au centre. Prenez autant de temps que nécessaire pour identifier la taille, la forme, l'orientation, le mouvement, la couleur, etc… Une fois ce travail effectué pour toutes les cibles présentées à l'écran contrôlez visuellement puis revenez au centre de l'écran et recommencez en essayant d'affiner votre perception. Appuyez sur la barre espace pour passer à l'essai suivant. Si vous souhaitez faire une pause ne cours d'exercice, appuyez sur echap. Appuyez sur la barre espace pour lancer l'exercice.",
  },
  ZPCCF: {
    id: 4,
    module: "Champ visuel",
    sous_module: "Zones Profondes Couleur Choix Forcé",
    parametre: {
      duree: [
        { value: "5", label: "5 min" },
        { value: "10", label: "10 min" },
        { value: "15", label: "15 min" },
        { value: "20", label: "20 min" },
        { value: "25", label: "25 min" },
        { value: "30", label: "30 min" },
        { value: "35", label: "35 min" },
        { value: "40", label: "40 min" },
        { value: "45", label: "45 min" },
        { value: "50", label: "50 min" },
        { value: "55", label: "55 min" },
        { value: "60", label: "60 min" },
      ],
      taille: [
        { value: "1", label: "Petit" },
        { value: "2", label: "Moyen" },
        { value: "3", label: "Grand" },
      ],
      side_aveugle: [
        { value: "droit", label: "Droit" },
        { value: "gauche", label: "Gauche" },
      ],
      mouvement: [
        { value: "true", label: "Oui" },
        { value: "false", label: "Non" },
      ],
    },
    instructions:
      "Fixez bien la croix centrale sans bouger les yeux. Des ronds de couleur vont apparaitre à gauche et à droite de la croix. Vous devez essayer de déterminer si le rond de droite et celui de gauche sont de la même couleur. Si vous souhaitez faire une pause en cours d'exercice, appuyez sur echap. Appuyez sur la barre espace pour lancer l'exercice.",
  },
  ZPFCF: {
    id: 5,
    module: "Champ visuel",
    sous_module: "Zones Profondes Formes Choix Forcé",
    parametre: {
      duree: [
        { value: "5", label: "5 min" },
        { value: "10", label: "10 min" },
        { value: "15", label: "15 min" },
        { value: "20", label: "20 min" },
        { value: "25", label: "25 min" },
        { value: "30", label: "30 min" },
        { value: "35", label: "35 min" },
        { value: "40", label: "40 min" },
        { value: "45", label: "45 min" },
        { value: "50", label: "50 min" },
        { value: "55", label: "55 min" },
        { value: "60", label: "60 min" },
      ],
      taille: [
        { value: "1", label: "Petit" },
        { value: "2", label: "Moyen" },
        { value: "3", label: "Grand" },
      ],
                  couleur_fond: [
        { value: "white", label: "Blanc" },
        { value: "black", label: "Noir" },
        { value: "grey", label: "Gris" },
      ],
            couleur_cible: [
        { value: "white", label: "Blanc" },
        { value: "colored", label: "Couleurs" },
      ],
      type_forme: [
        { value: "géométrique", label: "Géométrique" },
        { value: "barres", label: "Barres" },
        { value: "objets", label: "Objets" },
      ],
      mouvement: [
        { value: "true", label: "Oui" },
        { value: "false", label: "Non" },
      ],
    },
    instructions:
      "Fixez bien la croix centrale sans bouger les yeux. Plusieurs formes différentes vont apparaitre à gauche et à droite de la croix. Vous devez essayer de déterminer si la forme de droite et celle de gauche sont les mêmes. Si vous souhaitez faire une pause en cours d'exercice, appuyez sur echap. Appuyez sur la barre espace pour lancer l'exercice.",
  },
  CPD: {
    id: 6,
    module: "Exploration",
    sous_module: "Cibles parmi distracteurs",
    parametre: null,
    contenu: "120",
    instructions: null,
  },
  CN: {
    id: 7,
    module: "Exploration",
    sous_module: "Cibles numérotées",
    parametre: null,
    duree: null,
    contenu: null,
    instructions: null,
  },
  CET: {
    id: 8,
    module: "Exploration",
    sous_module: "Cherche et trouve",
    parametre: null,
    duree: null,
    contenu: null,
    instructions: null,
  },
  CGD: {
    id: 9,
    module: "Exploration",
    sous_module: "Comparaison Gauche/Droite",
    parametre: null,
    duree: null,
    contenu: null,
    instructions: null,
  },
  PVS: {
    id: 10,
    module: "Occulomotricité",
    sous_module: "Poursuite visuelle Smooth",
    parametre: {
      vitesse: [
        { value: "1", label: "1" },
        { value: "2", label: "2" },
        { value: "3", label: "3" },
      ],
      duree: [
        { value: "5", label: "5 min" },
        { value: "10", label: "10 min" },
        { value: "15", label: "15 min" },
        { value: "20", label: "20 min" },
        { value: "25", label: "25 min" },
        { value: "30", label: "30 min" },
        { value: "35", label: "35 min" },
        { value: "40", label: "40 min" },
        { value: "45", label: "45 min" },
        { value: "50", label: "50 min" },
        { value: "55", label: "55 min" },
        { value: "60", label: "60 min" },
      ],
    },
    instructions: null,
  },
  PVJ: {
    id: 11,
    module: "Occulomotricité",
    sous_module: "Poursuite visuelle Jump",
    parametre: null,
    duree: null,
    contenu: null,
    instructions: null,
  },
  OT: {
    id: 12,
    module: "Optocinétique",
    sous_module: "Lecture Texte",
    parametre: {
      vitesse: [
        // { value: "1", label: "1 cm/min" },
        // { value: "2", label: "2 cm/min" },
        { value: "3", label: "3 cm/min" },
        { value: "4", label: "4 cm/min" },
        { value: "5", label: "5 cm/min" },
        { value: "6", label: "6 cm/min" },
        { value: "7", label: "7 cm/min" },
        { value: "8", label: "8 cm/min" },
        // { value: "9", label: "9 cm/min" },
        // { value: "10", label: "10 cm/min" },
        // { value: "11", label: "11 cm/min" },
        // { value: "12", label: "12 cm/min" },
        // { value: "13", label: "13 cm/min" },
        // { value: "14", label: "14 cm/min" },
        // { value: "15", label: "15 cm/min" },
        // { value: "16", label: "16 cm/min" },
        // { value: "17", label: "17 cm/min" },
        // { value: "18", label: "18 cm/min" },
        // { value: "19", label: "19 cm/min" },
        // { value: "20", label: "20 cm/min" },
      ],
      duree: [
        { value: "5", label: "5 min" },
        { value: "10", label: "10 min" },
        { value: "15", label: "15 min" },
        { value: "20", label: "20 min" },
        { value: "25", label: "25 min" },
        { value: "30", label: "30 min" },
        { value: "35", label: "35 min" },
        { value: "40", label: "40 min" },
        { value: "45", label: "45 min" },
        { value: "50", label: "50 min" },
        { value: "55", label: "55 min" },
        { value: "60", label: "60 min" },
      ],
      contenu: [
        {
          value: "difficulte_1",
          label: "Difficulté 1",
          textes: [
            "Le Club des Cinq en embuscade",
            "Le Collier de verite - Sara Cone Bryant",
            "Le grand savant - Jean Macé",
            "Mademoiselle Sans-Soin - Jean Macé",
            "Pourquoi les arbres résineux gardent leurs feuilles...",
            "La Grenouille et le boeuf - Sara Cone Bryant",
            "Le Calife cigogne - Sara Cone Bryant",
            "Le Chat et le perroquet - Sara Cone Bryant",
          ],
        },
        {
          value: "difficulte_2",
          label: "Difficulté 2",
          textes: [
            "Mille et une nuits t1",
            "Voyage au centre de la Terre - Verne",
          ],
        },
        {
          value: "difficulte_3",
          label: "Difficulté 3",
          textes: ["Au Roi et Seigneurs", "Évolution créatrice"],
        },
      ],
    },
    instructions: null,
  },
  OB: {
    id: 13,
    module: "Optocinétique",
    sous_module: "Lecture Barres",
    parametre: {
      vitesse: [
        { value: "3", label: "3 cm/min" },
        { value: "4", label: "4 cm/min" },
        { value: "5", label: "5 cm/min" },
        { value: "6", label: "6 cm/min" },
        { value: "7", label: "7 cm/min" },
        { value: "8", label: "8 cm/min" },
      ],
      duree: [
        { value: "5", label: "5 min" },
        { value: "10", label: "10 min" },
        { value: "15", label: "15 min" },
        { value: "20", label: "20 min" },
        { value: "25", label: "25 min" },
        { value: "30", label: "30 min" },
        { value: "35", label: "35 min" },
        { value: "40", label: "40 min" },
        { value: "45", label: "45 min" },
        { value: "50", label: "50 min" },
        { value: "55", label: "55 min" },
        { value: "60", label: "60 min" },
      ],
      contenu: [
        {
          value: "difficulte_1",
          label: "Difficulté 1",
          textes: [
            "Le Club des Cinq en embuscade",
            "Le Collier de verite - Sara Cone Bryant",
            "Le grand savant - Jean Macé",
            "Mademoiselle Sans-Soin - Jean Macé",
            "Pourquoi les arbres résineux gardent leurs feuilles...",
            "La Grenouille et le boeuf - Sara Cone Bryant",
            "Le Calife cigogne - Sara Cone Bryant",
            "Le Chat et le perroquet - Sara Cone Bryant",
          ],
        },
        {
          value: "difficulte_2",
          label: "Difficulté 2",
          textes: [
            "Mille et une nuits t1",
            "Voyage au centre de la Terre - Verne",
          ],
        },
        {
          value: "difficulte_3",
          label: "Difficulté 3",
          textes: ["Au Roi et Seigneurs", "Évolution créatrice"],
        },
      ],
    },
    instructions: null,
  },
};

export const TitreChamp = {
  type_forme: "Forme de la cible",
  taille: "Taille de la cible",
  nombre_par_hemichamp: "Nombre de cible par hémichamp",
  couleur_cible: "Couleur de la cible",
  couleur_fond: "Couleur du fond",
  side: "Latéralisation de la cible",
  mouvement: "Mouvement des cibles",
  vitesse: "Vitesse du défilement",
  duree: "Durée de l'exercice",
  side_aveugle: "Latéralisation de la lésion",
  nombre_cible: "Nombre de cible",
  contenu: "Contenu de l'exercice",
};
